/**
 * LeftShield Portal - Controls Page
 * Displays overall compliance score, per-framework scorecards, and summary statistics
 * Route: /scorecard | Nav: Compliance > Scorecard
 */
import React, { useState } from 'react';
import {
  Title1,
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  tokens,
  Spinner,
  ProgressBar,
  Button,
  Tab,
  TabList,
  SelectTabEvent,
  SelectTabData,
} from '@fluentui/react-components';
import {
  ShieldCheckmarkRegular,
  DocumentRegular,
  ArrowDownloadRegular,
  ChevronDownRegular,
  ChevronUpRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi, complianceApi, reportsApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { ConfidenceBreakdownBar } from '../components/confidence';
import { ComplianceCoverageRadar } from '../components/compliance/ComplianceCoverageRadar';
import { PageHeader } from '../components/common/PageHeader';


type TabValue = 'summary' | 'byFramework' | 'history';

const getScoreColor = (score: number): string => {
  if (score >= 90) return tokens.colorPaletteGreenForeground1;
  if (score >= 70) return tokens.colorPaletteYellowForeground1;
  if (score >= 50) return tokens.colorPaletteRedForeground2;
  return tokens.colorPaletteRedForeground1;
};

const getScoreBarColor = (score: number): 'success' | 'warning' | 'error' | 'brand' => {
  if (score >= 90) return 'success';
  if (score >= 70) return 'warning';
  return 'error';
};

export const ScorecardPage: React.FC = () => {
  const ps = usePageStyles();
  const [selectedTab, setSelectedTab] = useState<TabValue>('summary');
  const [expandedFramework, setExpandedFramework] = useState<string | null>(null);
  const [exportPending, setExportPending] = useState(false);

  const { data: dashboard, isLoading: dashboardLoading } = useQuery({
    queryKey: ['complianceDashboard'],
    queryFn: () => dashboardApi.getComplianceDashboard(),
  });

  const { data: frameworks, isLoading: frameworksLoading } = useQuery({
    queryKey: ['complianceFrameworks'],
    queryFn: () => complianceApi.getFrameworks(),
  });

  const handleTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as TabValue);
  };

  const handleToggleFramework = (code: string) => {
    setExpandedFramework((prev) => (prev === code ? null : code));
  };

  const handleExportPdf = async () => {
    setExportPending(true);
    try {
      const blob = await reportsApi.generate({
        reportTypeId: 'compliance-scorecard',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'compliance-scorecard.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Unable to generate PDF report. Please try again later or contact support.');
    } finally {
      setExportPending(false);
    }
  };

  const isLoading = dashboardLoading || frameworksLoading;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner size="large" label="Loading scorecard data..." />
      </div>
    );
  }

  const scoreChange = dashboard?.scoreChange24h ?? 0;
  const overallScore = dashboard?.overallScore ?? 0;
  const enabledFrameworks = frameworks?.filter((f) => f.isEnabled) ?? [];

  return (
    <div className={ps.page}>
      <PageHeader
        title="Controls Effectiveness"
        subtitle="Scorecard of control effectiveness across your governance program"
        actions={
          <Button
            appearance="primary"
            icon={<ArrowDownloadRegular />}
            onClick={handleExportPdf}
            disabled={exportPending}
          >
            {exportPending ? 'Exporting...' : 'Export PDF'}
          </Button>
        }
      />

      {/* Overall Compliance Score Gauge */}
      <Card>
        <CardHeader
          image={
            <ShieldCheckmarkRegular
              style={{ fontSize: '2rem', color: getScoreColor(overallScore) }}
            />
          }
          header={<Title2>Overall Compliance Score</Title2>}
          description={
            <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
              Aggregated score across all enabled frameworks
            </Body2>
          }
        />
        <div className={ps.cardBody}>
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXL }}>
            <Title1
              className="metric-value"
              style={{
                fontSize: '3.5rem',
                lineHeight: '1',
                color: getScoreColor(overallScore),
              }}
            >
              {overallScore}%
            </Title1>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
              <ProgressBar
                value={overallScore / 100}
                thickness="large"
                color={getScoreBarColor(overallScore)}
              />
              <div className={ps.inlineRow}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>24h trend:</Body2>
                <Badge
                  appearance="filled"
                  color={scoreChange >= 0 ? 'success' : 'danger'}
                >
                  {scoreChange >= 0 ? '+' : ''}{scoreChange}%
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <Card>
        <div style={{ borderBottom: `1px solid ${tokens.colorNeutralStroke1}` }}>
          <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
            <Tab value="summary" icon={<ShieldCheckmarkRegular />}>
              Summary
            </Tab>
            <Tab value="byFramework" icon={<DocumentRegular />}>
              By Framework
            </Tab>
            <Tab value="history">
              History
            </Tab>
          </TabList>
        </div>

        {/* Summary Tab */}
        {selectedTab === 'summary' && (
          <div className={ps.cardBody}>
            {/* Stat Tiles */}
            <div className={ps.statGrid}>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Total Frameworks</Body2>
                <Title2 className="metric-value">{dashboard?.totalFrameworks ?? 0}</Title2>
              </div>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Enabled</Body2>
                <Title2 className="metric-value" style={{ color: tokens.colorPaletteGreenForeground1 }}>
                  {dashboard?.enabledFrameworks ?? 0}
                </Title2>
              </div>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Critical Findings</Body2>
                <Title2 className="metric-value" style={{ color: (dashboard?.criticalFindings ?? 0) > 0 ? tokens.colorPaletteRedForeground1 : tokens.colorNeutralForeground4 }}>
                  {dashboard?.criticalFindings ?? 0}
                </Title2>
              </div>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>High Findings</Body2>
                <Title2 className="metric-value" style={{ color: (dashboard?.highFindings ?? 0) > 0 ? tokens.colorPaletteRedForeground2 : tokens.colorNeutralForeground4 }}>
                  {dashboard?.highFindings ?? 0}
                </Title2>
              </div>
            </div>

            {/* Confidence Breakdown */}
            <div style={{ marginTop: tokens.spacingVerticalM }}>
              <Body2 style={{ color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalS }}>
                Confidence Breakdown
              </Body2>
              <ConfidenceBreakdownBar
                subscores={{ ec: 85, efc: 78, ei: 90, os: 72, rd: 65, bre: 80 }}
              />
            </div>

            {/* Framework Coverage */}
            <Card>
              <CardHeader header={<Title2>Framework Coverage</Title2>} />
              <div className={ps.cardBody}>
                <ComplianceCoverageRadar />
              </div>
            </Card>

            {/* Per-Framework Scorecard Grid */}
            {frameworks && frameworks.length > 0 ? (
              <div className={ps.metricGrid}>
                {frameworks.map((fw) => {
                  const hasAssessment = fw.passedCount > 0 || fw.failedCount > 0 || (fw.complianceScore ?? 0) > 0;
                  return (
                    <Card key={fw.id}>
                      <div className={ps.cardBody}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
                          <Body1 style={{ fontWeight: 600 }}>{fw.name}</Body1>
                          <div className={ps.inlineRow}>
                            <Badge appearance="outline" size="small">
                              {fw.code}
                            </Badge>
                            <Badge appearance="tint" color="informative" size="small">
                              v{fw.version}
                            </Badge>
                            {!fw.isEnabled && (
                              <Badge appearance="tint" color="warning" size="small">
                                Disabled
                              </Badge>
                            )}
                          </div>
                        </div>
                        {hasAssessment ? (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                              <ProgressBar
                                value={(fw.complianceScore ?? 0) / 100}
                                thickness="large"
                                color={getScoreBarColor(fw.complianceScore ?? 0)}
                                style={{ flex: 1 }}
                              />
                              <Body1 style={{ fontWeight: 600, color: getScoreColor(fw.complianceScore ?? 0) }}>
                                {fw.complianceScore ?? 0}%
                              </Body1>
                            </div>
                            <div className={ps.inlineRow}>
                              <Badge appearance="filled" color="success" size="small">
                                {fw.passedCount} passed
                              </Badge>
                              <Badge appearance="filled" color="danger" size="small">
                                {fw.failedCount} failed
                              </Badge>
                            </div>
                          </>
                        ) : (
                          <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                            {fw.controlCount} controls — not yet assessed
                          </Body2>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Body1 className={ps.emptyState}>
                No compliance frameworks configured. Enable frameworks in the Compliance settings.
              </Body1>
            )}
          </div>
        )}

        {/* By Framework Tab */}
        {selectedTab === 'byFramework' && (
          <div className={ps.cardBody}>
            {enabledFrameworks.length > 0 ? (
              <div className={ps.rowList}>
                {enabledFrameworks.map((fw) => {
                  const hasAssessment = fw.passedCount > 0 || fw.failedCount > 0 || (fw.complianceScore ?? 0) > 0;
                  const notAssessed = fw.controlCount - fw.passedCount - fw.failedCount;
                  return (
                    <div key={fw.id}>
                      <div
                        className={ps.rowItem}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleToggleFramework(fw.code)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flex: 1 }}>
                          {expandedFramework === fw.code ? (
                            <ChevronUpRegular />
                          ) : (
                            <ChevronDownRegular />
                          )}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, flex: 1 }}>
                            <Body1 style={{ fontWeight: 600 }}>{fw.name}</Body1>
                            <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                              {fw.code} v{fw.version} — {fw.controlCount} controls
                            </Body2>
                          </div>
                        </div>
                        {hasAssessment ? (
                          <div className={ps.inlineRow}>
                            <ProgressBar
                              value={(fw.complianceScore ?? 0) / 100}
                              thickness="large"
                              color={getScoreBarColor(fw.complianceScore ?? 0)}
                              style={{ width: '120px' }}
                            />
                            <Body1 style={{ fontWeight: 600, color: getScoreColor(fw.complianceScore ?? 0), minWidth: '48px', textAlign: 'right' }}>
                              {fw.complianceScore ?? 0}%
                            </Body1>
                          </div>
                        ) : (
                          <Badge appearance="tint" color="informative" size="small">
                            Not assessed
                          </Badge>
                        )}
                      </div>

                      {/* Expanded Detail */}
                      {expandedFramework === fw.code && (
                        <Card style={{ marginTop: tokens.spacingVerticalS, marginLeft: tokens.spacingHorizontalXL }}>
                          <div className={ps.cardBody}>
                            {hasAssessment ? (
                              <>
                                <div className={ps.statGrid}>
                                  <div className={ps.statTile}>
                                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Compliance Score</Body2>
                                    <Title2 className="metric-value" style={{ color: getScoreColor(fw.complianceScore ?? 0) }}>
                                      {fw.complianceScore ?? 0}%
                                    </Title2>
                                  </div>
                                  <div className={ps.statTile}>
                                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Total Controls</Body2>
                                    <Title2 className="metric-value">{fw.controlCount}</Title2>
                                  </div>
                                  <div className={ps.statTile}>
                                    <Body2 style={{ color: tokens.colorPaletteGreenForeground1 }}>Passed</Body2>
                                    <Title2 className="metric-value" style={{ color: tokens.colorPaletteGreenForeground1 }}>
                                      {fw.passedCount}
                                    </Title2>
                                  </div>
                                  <div className={ps.statTile}>
                                    <Body2 style={{ color: tokens.colorPaletteRedForeground1 }}>Failed</Body2>
                                    <Title2 className="metric-value" style={{ color: tokens.colorPaletteRedForeground1 }}>
                                      {fw.failedCount}
                                    </Title2>
                                  </div>
                                </div>
                                {fw.controlCount > 0 && (
                                  <div>
                                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Control Breakdown</Body2>
                                    <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, marginTop: tokens.spacingVerticalS }}>
                                      <div
                                        style={{
                                          flex: fw.passedCount,
                                          height: '8px',
                                          borderRadius: tokens.borderRadiusSmall,
                                          backgroundColor: tokens.colorPaletteGreenBackground3,
                                        }}
                                      />
                                      <div
                                        style={{
                                          flex: fw.failedCount,
                                          height: '8px',
                                          borderRadius: tokens.borderRadiusSmall,
                                          backgroundColor: tokens.colorPaletteRedBackground3,
                                        }}
                                      />
                                      {notAssessed > 0 && (
                                        <div
                                          style={{
                                            flex: notAssessed,
                                            height: '8px',
                                            borderRadius: tokens.borderRadiusSmall,
                                            backgroundColor: tokens.colorNeutralBackground4,
                                          }}
                                        />
                                      )}
                                    </div>
                                    <div className={ps.inlineRow} style={{ marginTop: tokens.spacingVerticalS }}>
                                      <Badge appearance="filled" color="success" size="small">
                                        Passed: {fw.passedCount}
                                      </Badge>
                                      <Badge appearance="filled" color="danger" size="small">
                                        Failed: {fw.failedCount}
                                      </Badge>
                                      {notAssessed > 0 && (
                                        <Badge appearance="tint" color="informative" size="small">
                                          Not Assessed: {notAssessed}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {fw.lastAssessedAt && (
                                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                                    Last assessed: {new Date(fw.lastAssessedAt).toLocaleString()}
                                  </Body2>
                                )}
                              </>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
                                <div className={ps.statGrid}>
                                  <div className={ps.statTile}>
                                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Total Controls</Body2>
                                    <Title2>{fw.controlCount}</Title2>
                                  </div>
                                </div>
                                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                                  No assessment data yet. Run a compliance scan to generate scores for this framework.
                                </Body2>
                              </div>
                            )}
                          </div>
                        </Card>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <Body1 className={ps.emptyState}>
                No enabled frameworks. Enable frameworks to view detailed scorecards.
              </Body1>
            )}
          </div>
        )}

        {/* History Tab */}
        {selectedTab === 'history' && (
          <div className={ps.cardBody}>
            <Body1 className={ps.emptyState}>
              Historical tracking activates after your first compliance assessment.
            </Body1>
          </div>
        )}
      </Card>
    </div>
  );
};
